import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence, useMotionValue, useDragControls } from "framer-motion";
import { X, ChevronRight, Send, Loader2, Mic, Check, CalendarPlus, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";
import { createRecorder, extForMime, logRecorderError, micErrorMessage } from "../lib/recorder";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { isPremium, startProCheckout } from "../lib/premium";

// Oci — mascota jaguar de ClinPed. Vive: parpadea, bosteza, salta, ruge y cambia de atuendo por sección.
const OCI = {
  wave: "/oci/wave.png",
  think: "/oci/think.png",
  listen: "/oci/listen.png",
  happy: "/oci/happy.png",
  surgeon: "/oci/surgeon.png",
  head: "/oci/head.png",
  blink: "/oci/blink.png",
  yawn: "/oci/yawn.png",
  roar: "/oci/roar.png",
  explorer: "/oci/explorer.png",
  astronaut: "/oci/astronaut.png",
  athlete: "/oci/athlete.png",
};

// Variantes por edad del paciente (adulto 18-49, mayor 50+).
const OCI_ADULT = { wave: "/oci/adult_wave.png", think: "/oci/adult_think.png", happy: "/oci/adult_happy.png", listen: "/oci/adult_wave.png", head: "/oci/adult_head.png" };
const OCI_ELDER = { wave: "/oci/elder_wave.png", think: "/oci/elder_think.png", happy: "/oci/elder_happy.png", listen: "/oci/elder_wave.png", head: "/oci/elder_head.png" };
// Oci de dientes: exclusivo para suscriptores ClinDent (plan dental / has_odontogram).
const OCI_DENTAL = { wave: "/oci/dental_wave.png", think: "/oci/dental_think.png", happy: "/oci/dental_happy.png", listen: "/oci/dental_wave.png", head: "/oci/dental_head.png" };

function ageYearsFromISO(iso) {
  if (!iso) return null;
  const bd = new Date(iso);
  if (isNaN(bd)) return null;
  return (Date.now() - bd.getTime()) / (365.25 * 24 * 3600 * 1000);
}

// Oci cambia de pose/atuendo según la sección, la pestaña y la acción (no por tiempo).
function poseFor(pathname, tab) {
  if (pathname.startsWith("/agenda")) return OCI.explorer;
  if (pathname.startsWith("/estadisticas")) return OCI.astronaut;
  if (pathname.startsWith("/equipo")) return OCI.athlete;
  if (pathname.startsWith("/pacientes/")) {
    const m = {
      citas: OCI.surgeon,
      graficas: OCI.explorer,
      recetas: OCI.happy,
      ia: OCI.think,
      archivos: OCI.astronaut,
      vacunas: OCI.roar,
      consentimiento: OCI.surgeon,
      hitos: OCI.athlete,
    };
    return m[tab] || OCI.wave;
  }
  return OCI.wave; // pediatra por defecto
}

const TOUR = [
  { t: "¡Hola! Soy Oci 🐆", d: "Tu amigo jaguar y asistente clínico con IA. Puedes escribirme o dictarme y te respondo, incluso sobre el paciente que tengas abierto." },
  { t: "Arrástrame donde quieras", d: "Muéveme a la esquina que prefieras para que no estorbe en tus capturas o lecturas. Recuerdo mi posición." },
  { t: "Pregúntame lo que sea", d: "Ej.: «¿por qué vino la última vez?», dosis, planes de manejo. Conozco el expediente abierto." },
  { t: "Al finalizar", d: "Reviso la nota según la NOM-004 y te sugiero mejoras antes de cerrar la consulta." },
];

function tipFor(pathname, tab) {
  if (pathname.startsWith("/pacientes/")) {
    const map = {
      expediente: "Esta es la carpeta del paciente. Toca «Abrir PDF» para revisar una consulta o inicia una nueva.",
      consultas: "Historia clínica: captura antecedentes, somatometría y signos vitales. Usa los botones IA para autollenar y asignar CIE-10.",
      graficas: "Compara el crecimiento contra CDC/WHO. El punto del paciente debe seguir las curvas.",
      recetas: "Pulsa el botón IA de cada fármaco para calcular la dosis automáticamente.",
      citas: "Agenda las próximas citas del paciente. Con un toque puedes enviarle el recordatorio al tutor por WhatsApp desde tu propio teléfono.",
      archivos: "Laboratorios: sube el PDF, escanea con la cámara o escribe/dicta una nota. Puedes analizar cualquier resultado con IA.",
      consentimiento: "Genera consentimientos informados (con IA opcional), guárdalos como plantilla y compártelos por QR con firma del tutor.",
      ia: "Usa «Consulta libre» para preguntarle a la IA. Al finalizar reviso la nota (NOM-004).",
      vacunas: "Cartilla y vacunas recomendadas por edad, en una sola pestaña. Las recomendaciones se guardan para ahorrar IA.",
      hitos: "Verifica los hitos del desarrollo esperados para la edad.",
    };
    return map[tab] || "Estás en el expediente del paciente. Pregúntame lo que necesites.";
  }
  if (pathname.startsWith("/pacientes")) return "Esta es tu biblioteca de expedientes. Abre un paciente para ver su carpeta.";
  if (pathname.startsWith("/perfil")) return "Completa tus datos (NOM-004) para que aparezcan en tus recetas.";
  if (pathname.startsWith("/dashboard")) return "Bienvenido. ¿Listo para la próxima consulta? Puedes preguntarme lo que sea.";
  return "Estoy aquí para ayudarte. Escríbeme tu duda clínica.";
}

export default function ClinPi() {
  const location = useLocation();
  const { user } = useAuth();
  const premium = isPremium(user);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const h = (e) => { if (e.detail === "oci") { setOpen(true); } };
    window.addEventListener("clingo-open", h);
    return () => window.removeEventListener("clingo-open", h);
  }, []);
  const [tourStep, setTourStep] = useState(-1);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [hint, setHint] = useState(false);
  const [recording, setRecording] = useState(false);
  const [voiceBusy, setVoiceBusy] = useState(false);
  const [pendingAppt, setPendingAppt] = useState(null);
  const [celebrate, setCelebrate] = useState(false);
  const scrollRef = useRef(null);
  const hintTimer = useRef(null);
  const mediaRec = useRef(null);
  const holdTimer = useRef(null);
  const didRecord = useRef(false);
  const dragging = useRef(false);
  const orbRef = useRef(null);
  const [anchor, setAnchor] = useState({ side: "right", vpos: "up" });
  const [ageBand, setAgeBand] = useState("kid"); // 'kid' | 'adult' | 'elder'
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const computeAnchor = () => {
    const el = orbRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setAnchor({
      side: (r.left + r.width / 2) > window.innerWidth / 2 ? "left" : "right",
      vpos: (r.top + r.height / 2) < window.innerHeight / 2 ? "down" : "up",
    });
  };

  // Posición arrastrable (recordada).
  const savedPos = (() => { try { return JSON.parse(localStorage.getItem("oci_pos") || "null"); } catch { return null; } })();
  const x = useMotionValue(savedPos?.x || 0);
  const y = useMotionValue(savedPos?.y || 0);
  const dragControls = useDragControls();
  const savePos = () => localStorage.setItem("oci_pos", JSON.stringify({ x: x.get(), y: y.get() }));

  const tab = new URLSearchParams(location.search).get("tab") || (location.pathname.startsWith("/pacientes/") ? "expediente" : "");
  const patientMatch = location.pathname.match(/\/pacientes\/([^/]+)/);
  const patientId = patientMatch ? patientMatch[1] : null;

  // Pose por sección/pestaña; la acción manda (escuchar/pensar/feliz).
  // Prioridad de persona: ClinDent (dental) > edad del paciente (adulto/mayor) > niño (disfraces por sección).
  const dentalSet = user?.has_odontogram ? OCI_DENTAL : null;
  const ageSet = dentalSet || (ageBand === "elder" ? OCI_ELDER : ageBand === "adult" ? OCI_ADULT : null);
  const sectionPose = poseFor(location.pathname, tab);
  const face = ageSet
    ? (recording ? ageSet.listen : (loading || voiceBusy) ? ageSet.think : celebrate ? ageSet.happy : ageSet.wave)
    : (recording ? OCI.listen : (loading || voiceBusy) ? OCI.think : celebrate ? OCI.happy : sectionPose);
  const headImg = ageSet ? ageSet.head : OCI.head;

  useEffect(() => {
    if (!localStorage.getItem("oci_tour_done")) {
      const t = setTimeout(() => { setTourStep(0); setOpen(true); }, 900);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  useEffect(() => {
    setMessages([]); setInput(""); setPendingAppt(null);
    if (!patientId) { setAgeBand("kid"); return; }
    api.get(`/patients/${patientId}`).then((r) => {
      const y = ageYearsFromISO(r.data?.birth_date);
      setAgeBand(y == null ? "kid" : y >= 50 ? "elder" : y >= 18 ? "adult" : "kid");
    }).catch(() => setAgeBand("kid"));
  }, [patientId]);

  useEffect(() => {
    if (tourStep >= 0) return;
    if (!localStorage.getItem("oci_tour_done")) return;
    computeAnchor();
    setHint(true);
    clearTimeout(hintTimer.current);
    hintTimer.current = setTimeout(() => setHint(false), 6500);
    return () => clearTimeout(hintTimer.current);
  }, [location.pathname, tab]); // eslint-disable-line

  const flashHappy = () => { setCelebrate(true); setTimeout(() => setCelebrate(false), 2200); };


  const closeChat = () => {
    setOpen(false);
    if (inTour) closeTour();
    if (recording) stopRecording();
    setMessages([]); setInput(""); setPendingAppt(null);
  };

  const closeTour = () => { setTourStep(-1); localStorage.setItem("oci_tour_done", "1"); };
  const inTour = tourStep >= 0;
  const tip = tipFor(location.pathname, tab);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    const next = [...messages, { role: "user", content: text }];
    setMessages(next); setInput(""); setLoading(true);
    try {
      const res = await api.post("/ai/chat", { message: text, patient_id: patientId, history: messages });
      setMessages([...next, { role: "assistant", content: res.data.text }]);
      flashHappy();
    } catch (e) {
      setMessages([...next, { role: "assistant", content: "No pude responder en este momento. Intenta de nuevo." }]);
    } finally {
      setLoading(false);
    }
  };

  const processVoice = async (blob) => {
    setVoiceBusy(true); setOpen(true);
    try {
      const fd = new FormData();
      fd.append("file", new File([blob], `voz.${extForMime(blob.type)}`, { type: blob.type || "audio/webm" }));
      const tr = await api.post("/ai/voice", fd);
      const text = (tr.data.text || "").trim();
      if (!text) { toast.error("No se escuchó nada. Intenta de nuevo."); setVoiceBusy(false); return; }
      const histBefore = messages;
      setMessages((m) => [...m, { role: "user", content: text }]);
      const res = await api.post("/ai/voice-command", { text, patient_id: patientId, history: histBefore });
      const d = res.data;
      if (d.action === "agendar") setPendingAppt(d.draft);
      else if (d.action === "sin_paciente" || d.action === "ambiguo") setMessages((m) => [...m, { role: "assistant", content: d.message }]);
      else { setMessages((m) => [...m, { role: "assistant", content: d.text || "" }]); flashHappy(); }
    } catch (e) {
      toast.error("No se pudo procesar la voz");
    } finally {
      setVoiceBusy(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = createRecorder(stream);
      mr.onerror((e) => logRecorderError("recorder onerror", e, mr));
      mr.onstop = async (blob) => {
        stream.getTracks().forEach((t) => t.stop());
        if (blob.size > 800) await processVoice(blob);
      };
      mr.start();
      mediaRec.current = mr;
      setRecording(true); setOpen(true);
    } catch (e) {
      logRecorderError("startRecording", e, mediaRec.current);
      toast.error(micErrorMessage(e));
    }
  };

  const stopRecording = () => {
    if (mediaRec.current) mediaRec.current.stop();
    setRecording(false);
  };

  const toggleMic = () => {
    if (!premium) { toast.info("Oci por voz (dictado) es del Plan Pro. Actualiza para activarlo.", { action: { label: "Actualizar", onClick: startProCheckout } }); return; }
    if (recording) stopRecording(); else startRecording();
  };

  const confirmAppt = async () => {
    if (!pendingAppt) return;
    try {
      await api.post("/appointments", {
        patient_id: pendingAppt.patient_id, scheduled_at: pendingAppt.scheduled_at,
        reason: pendingAppt.reason, type: pendingAppt.type,
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      setMessages((m) => [...m, { role: "assistant", content: `✅ Cita agendada para ${pendingAppt.patient_name}.` }]);
      toast.success("Cita agendada");
      window.dispatchEvent(new Event("clinped:appointments-updated"));
      setPendingAppt(null); flashHappy();
    } catch (e) { toast.error("No se pudo agendar la cita"); }
  };

  const fmtApptDate = (iso) => { try { return new Date(iso).toLocaleString("es-MX", { weekday: "long", day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };
  const TYPE_LABEL = { primera_vez: "Primera vez", seguimiento: "Seguimiento", vacunacion: "Vacunación" };

  // Tap corto = abrir/cerrar; mantener presionado = grabar voz; arrastrar = mover.
  const onOrbDown = (e) => {
    didRecord.current = false;
    dragControls.start(e);
    if (premium) holdTimer.current = setTimeout(() => { didRecord.current = true; startRecording(); }, 380);
  };
  const onOrbUp = () => {
    clearTimeout(holdTimer.current);
    if (dragging.current) return;
    if (didRecord.current) { stopRecording(); return; }
    computeAnchor();
    if (inTour) { closeTour(); setOpen(true); }
    else setOpen((o) => { if (!o) { setMessages([]); setInput(""); setPendingAppt(null); } return !o; });
  };

  const bubbleHoriz = anchor.side === "left" ? "right-[4.75rem]" : "left-[4.75rem]";
  const bubbleVert = anchor.vpos === "down" ? "top-0" : "bottom-0";

  return (
    <motion.div
      drag
      dragListener={false}
      dragControls={dragControls}
      dragMomentum={false}
      dragElastic={0}
      style={isMobile ? {} : { x, y }}
      onDragStart={() => { dragging.current = true; clearTimeout(holdTimer.current); }}
      onDragEnd={() => { savePos(); computeAnchor(); setTimeout(() => (dragging.current = false), 40); }}
      className={`fixed bottom-6 left-6 lg:left-[17rem] ${open && isMobile ? "z-[95]" : "z-[60]"}`}
    >
      {/* Lanzador: Oci dentro de un círculo (como ClinPi), con poses variadas */}
      <motion.button
        ref={orbRef}
        data-testid="clinpi-orb"
        onPointerDown={onOrbDown}
        onPointerUp={onOrbUp}
        onPointerLeave={() => { clearTimeout(holdTimer.current); if (recording) stopRecording(); }}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.92 }}
        className="relative w-16 h-16 rounded-full shrink-0 select-none touch-none cursor-grab active:cursor-grabbing hidden md:block"
        aria-label="Asistente Oci (arrástrame · mantén presionado para hablar)"
        title="Toca para abrir · mantén presionado para hablar · arrástrame"
      >
        {/* halo suave */}
        <motion.span className="absolute -inset-1 rounded-full"
          style={{ background: "radial-gradient(circle at 50% 50%, rgba(0,180,203,0.40), rgba(13,71,161,0) 70%)" }}
          animate={{ scale: [1, 1.15, 1], opacity: [0.6, 0.3, 0.6] }}
          transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }} />
        {recording && (
          <motion.span className="absolute -inset-1.5 rounded-full bg-red-500/40"
            animate={{ scale: [1, 1.4, 1], opacity: [0.7, 0, 0.7] }}
            transition={{ duration: 1.1, repeat: Infinity, ease: "easeInOut" }} />
        )}
        {/* círculo blanco con Oci recortado a la cara/torso */}
        <span className="absolute inset-0 rounded-full bg-white shadow-xl ring-2 ring-[#00B4CB]/60 overflow-hidden">
          <AnimatePresence mode="popLayout">
            <motion.img
              key={face}
              src={face}
              alt="Oci"
              draggable={false}
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35 }}
              style={{ objectPosition: "50% 20%" }}
              className="absolute inset-0 w-full h-full object-cover pointer-events-none scale-110"
            />
          </AnimatePresence>
        </span>
      </motion.button>

      <AnimatePresence>
        {hint && !open && !inTour && (
          <motion.div
            data-testid="clinpi-hint"
            initial={{ opacity: 0, y: 8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.95 }}
            className={`absolute ${bubbleHoriz} ${bubbleVert} w-[16rem] bg-white dark:bg-[#1a1b26] rounded-2xl border border-stone-100 shadow-xl p-3 hidden md:block`}
          >
            <div className="flex items-start justify-between gap-2">
              <p className="font-outfit font-bold text-[#0D47A1] text-xs flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#00B4CB] animate-pulse" /> Oci
              </p>
              <button data-testid="clinpi-hint-close" onClick={() => setHint(false)}
                className="text-muted-foreground hover:text-destructive transition-colors -mt-0.5"><X size={13} /></button>
            </div>
            <p className="text-xs text-brand-ink/80 leading-relaxed mt-1">{tip}</p>
            <button data-testid="clinpi-hint-open" onClick={() => { setHint(false); setOpen(true); }}
              className="mt-2 text-[11px] font-bold text-[#00B4CB] hover:underline flex items-center gap-1">
              Preguntar a Oci <ChevronRight size={12} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && isMobile && (
          <motion.div key="cp-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={closeChat} className="fixed inset-0 bg-black/40 z-[94]" />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="clinpi-bubble"
            initial={{ opacity: 0, scale: 0.9, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 10 }}
            className={isMobile
              ? "fixed inset-x-0 bottom-4 mx-auto w-[94vw] max-w-[26rem] z-[95] bg-white dark:bg-[#1a1b26] rounded-2xl border border-stone-100 shadow-2xl flex flex-col overflow-hidden"
              : `absolute ${bubbleHoriz} ${bubbleVert} w-[20rem] sm:w-[22rem] bg-white dark:bg-[#1a1b26] rounded-2xl border border-stone-100 shadow-2xl flex flex-col overflow-hidden`}
            style={{ maxHeight: "80vh" }}
          >
            <div className="flex items-center justify-between gap-2 p-3 border-b border-stone-100 bg-gradient-to-r from-[#0D47A1] to-[#00B4CB]">
              <p className="font-outfit font-bold text-white text-sm flex items-center gap-2">
                <img src={headImg} alt="Oci" className="w-8 h-8 rounded-full object-cover bg-white ring-2 ring-white/60" />
                Oci
                {patientId && !inTour && <span className="text-[10px] font-semibold text-white bg-white/25 px-1.5 py-0.5 rounded-full">expediente</span>}
              </p>
              <button data-testid="clinpi-close" onClick={closeChat}
                className="text-white/80 hover:text-white transition-colors"><X size={16} /></button>
            </div>

            {inTour ? (
              <div className="p-4">
                <div className="flex justify-center mb-2">
                  <motion.img src={OCI.wave} alt="Oci" className="h-24 w-24 object-contain"
                    animate={{ rotate: [-4, 4, -4] }} transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }} />
                </div>
                <p className="font-outfit font-semibold text-[#0D47A1] text-sm">{TOUR[tourStep].t}</p>
                <p className="text-sm text-muted-foreground mt-1">{TOUR[tourStep].d}</p>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex gap-1">
                    {TOUR.map((_, i) => <span key={i} className={`w-1.5 h-1.5 rounded-full ${i === tourStep ? "bg-[#00B4CB]" : "bg-stone-200"}`} />)}
                  </div>
                  <button data-testid="clinpi-tour-next"
                    onClick={() => { if (tourStep < TOUR.length - 1) setTourStep(tourStep + 1); else { closeTour(); } }}
                    className="flex items-center gap-1 text-xs font-bold text-white bg-[#00B4CB] px-3 py-1.5 rounded-full hover:opacity-90 transition-all">
                    {tourStep < TOUR.length - 1 ? "Siguiente" : "¡Listo!"} <ChevronRight size={13} />
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div ref={scrollRef} data-testid="clinpi-messages" className="flex-1 overflow-y-auto p-3 space-y-3 min-h-[120px]">
                  <div className="bg-[#00B4CB]/10 rounded-xl rounded-tl-sm px-3 py-2 text-sm text-brand-ink/80 leading-relaxed">{tip}</div>
                  {messages.map((m, i) => (
                    <div key={i} className={`flex items-end gap-1.5 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                      {m.role !== "user" && <img src={headImg} alt="Oci" className="w-7 h-7 rounded-full object-cover bg-white ring-1 ring-[#00B4CB]/40 shrink-0 mb-0.5" />}
                      <div className={`max-w-[82%] px-3 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[#0D47A1] text-white rounded-br-sm" : "bg-stone-100 text-brand-ink rounded-bl-sm"}`}>
                        {m.content}
                      </div>
                    </div>
                  ))}
                  {loading && (
                    <div className="flex justify-start"><div className="bg-stone-100 px-3 py-2 rounded-2xl rounded-bl-sm"><Loader2 size={15} className="animate-spin text-[#00B4CB]" /></div></div>
                  )}
                  {recording && (
                    <div className="flex items-center gap-2 text-xs font-semibold text-red-500" data-testid="clinpi-recording">
                      <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" /> Escuchando… suelta a Oci o toca el micrófono para enviar
                    </div>
                  )}
                  {voiceBusy && (
                    <div className="flex items-center gap-2 text-xs text-[#00B4CB]" data-testid="clinpi-voice-busy"><Loader2 size={13} className="animate-spin" /> Transcribiendo…</div>
                  )}
                  {pendingAppt && (
                    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                      data-testid="clinpi-appt-confirm" className="bg-[#00B4CB]/10 border border-[#00B4CB]/30 rounded-2xl p-3">
                      <p className="text-xs font-bold uppercase tracking-wide text-[#0D47A1] flex items-center gap-1.5"><CalendarPlus size={13} /> Confirmar cita</p>
                      <p className="text-sm text-brand-ink font-semibold mt-1.5">{pendingAppt.patient_name}</p>
                      <p className="text-sm text-brand-ink/80 capitalize">{fmtApptDate(pendingAppt.scheduled_at)}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{TYPE_LABEL[pendingAppt.type] || pendingAppt.type}{pendingAppt.reason ? ` · ${pendingAppt.reason}` : ""}</p>
                      <div className="flex gap-2 mt-2.5">
                        <button data-testid="clinpi-appt-save" onClick={confirmAppt}
                          className="flex-1 flex items-center justify-center gap-1.5 bg-[#00B4CB] text-white text-xs font-bold py-2 rounded-xl hover:opacity-90 transition-all"><Check size={14} /> Agendar</button>
                        <button data-testid="clinpi-appt-cancel" onClick={() => setPendingAppt(null)}
                          className="px-3 bg-white border border-stone-200 text-brand-ink text-xs font-bold py-2 rounded-xl">Cancelar</button>
                      </div>
                    </motion.div>
                  )}
                </div>
                <div className="px-2.5 py-1.5 border-t border-amber-100 bg-amber-50/70 flex items-start gap-1.5 text-[10px] leading-tight text-amber-800/90" data-testid="clinpi-ai-disclaimer">
                  <AlertTriangle size={11} className="text-amber-500 shrink-0 mt-px" />
                  <span>Apoyo con IA: puede contener errores; no reemplaza el criterio médico.</span>
                </div>
                <div className="p-2.5 border-t border-stone-100 flex items-center gap-2">
                  <input
                    data-testid="clinpi-input"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") send(); }}
                    placeholder={recording ? "Escuchando…" : (patientId ? "Pregúntame o dicta…" : "Escríbeme o dicta…")}
                    className="flex-1 bg-stone-50 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#00B4CB]/40"
                  />
                  <button data-testid="clinpi-mic" onClick={toggleMic} title="Oci por voz · dictado"
                    className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all ${recording ? "bg-red-500 text-white animate-pulse" : "bg-[#00B4CB]/15 text-[#00B4CB] hover:bg-[#00B4CB]/25"}`}>
                    <Mic size={16} />
                  </button>
                  <button data-testid="clinpi-send" onClick={send} disabled={loading || !input.trim()}
                    className="w-9 h-9 rounded-xl bg-[#0D47A1] text-white flex items-center justify-center disabled:opacity-50 hover:opacity-90 transition-all">
                    <Send size={16} />
                  </button>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
